/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { map, get } from 'lodash';
import { Icon, Message, Form, Input, Label } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Modal, Button, FormLabel } from '../../../../../components';
import ServiceTypeSelect from './ServiceTypeSelect';
import { GET_SERVICE_GROUPS_QUERY } from '../../../containers/service/ServicesList';

type AddServiceGroupDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
};

const intlMessages = defineMessages({
    serviceGroupNamePlaceholder: {
        id: 'pages.settings.services.groupForm.placeholder.name',
        defaultMessage: 'Group name',
    },
    groupNameError: {
        id: 'pages.settings.services.groupForm.groupNameError',
        defaultMessage: 'Enter group name',
    },
    nameExistingsError: {
        id: 'pages.settings.services.groupForm.nameExistingsError',
        defaultMessage: 'Service group with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const CREATE_SERVICE_GROUP_MUTATION = gql`
    mutation createServiceGroup(
        $input: CreateServiceGroupInput!
        $lang: String!
    ) {
        createServiceGroup(input: $input) {
            serviceGroup {
                id
                name(lang: $lang)
                nameVariants {
                    lv
                    ru
                    en
                }
                type
                services {
                    id
                }
            }
        }
    }
`;

const AddServiceGroupDialog = ({
    trigger,
    open,
    onClose,
    intl,
}: AddServiceGroupDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );
    const languages = useSelector(({ intl: intlState }) =>
        intlState.get('languages'),
    );
    const defaultLanguage = useSelector(({ intl: intlState }) =>
        intlState.get('defaultLanguage'),
    );

    const buildDefaultValues = () => ({
        nameVariants: (languages || []).reduce(
            (acc, lang) => ({ ...acc, [lang]: '' }),
            {},
        ),
        type: null,
    });

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: buildDefaultValues() });

    useEffect(() => {
        if (open) {
            reset(buildDefaultValues());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reset]);

    const [createServiceGroup] = useMutation(CREATE_SERVICE_GROUP_MUTATION);

    const onSubmit = async formData => {
        try {
            await createServiceGroup({
                variables: {
                    input: {
                        salon,
                        ...formData,
                        type: formData.type || null,
                    },
                    lang: currentLanguage,
                },
                update: (store, { data: { createServiceGroup: result } }) => {
                    const data = store.readQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables: {
                            salon,
                            lang: currentLanguage,
                        },
                    });
                    if (
                        data &&
                        data.viewer &&
                        data.viewer.salon &&
                        data.viewer.salon.serviceGroups
                    ) {
                        store.writeQuery({
                            query: GET_SERVICE_GROUPS_QUERY,
                            variables: {
                                salon,
                                lang: currentLanguage,
                            },
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: {
                                        ...data.viewer.salon,
                                        serviceGroups: {
                                            ...data.viewer.salon.serviceGroups,
                                            nodes: [
                                                ...data.viewer.salon.serviceGroups.nodes,
                                                result.serviceGroup,
                                            ],
                                        },
                                    },
                                },
                            },
                        });
                    }
                },
            });
            onClose();
        } catch (error) {
            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
            <Modal.Header>
                <FormattedMessage
                    id="pages.settings.services.groupForm.addServiceGroup"
                    defaultMessage="Add Service Group"
                />
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.settings.services.serviceForm.name"
                            defaultMessage="Name"
                        />
                    </FormLabel>
                    <Form.Group widths="equal">
                        {map(languages, lang => (
                            <Form.Field
                                key={lang}
                                error={
                                    !!(
                                        errors.nameVariants &&
                                        errors.nameVariants[lang]
                                    )
                                }
                            >
                                <FormLabel>{lang.toUpperCase()}</FormLabel>
                                <Controller
                                    name={`nameVariants.${lang}`}
                                    control={control}
                                    rules={
                                        lang === defaultLanguage
                                            ? {
                                                  required: intl.formatMessage(
                                                      intlMessages.groupNameError,
                                                  ),
                                              }
                                            : {}
                                    }
                                    render={({ field: { ref: _ref, ...field } }) => (
                                        <Input
                                            {...field}
                                            placeholder={intl.formatMessage(
                                                intlMessages.serviceGroupNamePlaceholder,
                                            )}
                                            fluid
                                        />
                                    )}
                                />
                                {errors.nameVariants &&
                                    errors.nameVariants[lang] && (
                                        <Label color="red" pointing basic>
                                            {errors.nameVariants[lang].message}
                                        </Label>
                                    )}
                            </Form.Field>
                        ))}
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <FormattedMessage
                                id="pages.settings.services.groupForm.placeholder.type"
                                defaultMessage="Group type"
                            >
                                {placeholder => (
                                    <Controller
                                        name="type"
                                        control={control}
                                        render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                            <ServiceTypeSelect
                                                {...field}
                                                value={value || undefined}
                                                onChange={(e, data) =>
                                                    onChange(data.value)
                                                }
                                                placeholder={placeholder}
                                            />
                                        )}
                                    />
                                )}
                            </FormattedMessage>
                        </Form.Field>
                    </Form.Group>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    primary
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.settings.services.groupForm.buttons.add"
                        defaultMessage="Add"
                    />
                </Button>
                <Button disabled={isSubmitting} onClick={onClose} basic>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.settings.services.groupForm.buttons.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(AddServiceGroupDialog);
